/* Libraries */
import React, { useRef, SyntheticEvent, useState } from 'react';
import Link from 'next/link';
import cn from 'classnames';

/* Components */
import SkuCategoryItems, { SkuCategoryItem } from './Parts/SkuCategoryItems';
import SubCategorySlide from './Parts/SubCategorySlide';

/* Hooks */
import useOutsideClick from '@hooks/useOutsideClick';

/* Constants */
import { skuCatPath } from '@constants/paths';

/* Types */
import { CategoryProps, ISecondNav, SkuCategoriesProps } from './topNavItems.types';
import { ICategory, ISubCategory } from 'src/constants/nav';

/* Styles */
import styles from './topnavitems.module.scss';

const Category = ({ category, onSecondNavChange }: CategoryProps) => {
  const columnGroupName = category ? (category.name || '').replace(/[^\w&]/g, '') : '';
  const lowerColumnGroupName = columnGroupName.toLowerCase();

  return (
    <div className={styles.categoryContainer} data-testid={`navigation-menu-column-group-${lowerColumnGroupName}`}>
      {category.seoName ? (
        <Link
          href={skuCatPath(category.seoName)}
          className={styles.categoryTitle}
          data-testid={`navigation-menu-sku-category-${lowerColumnGroupName}`}
        >
          {category.name}
        </Link>
      ) : (
        <div data-testid={`navigation-menu-sku-category-${lowerColumnGroupName}`} className={cn('h6', styles.categoryTitle)}>
          {category.name}
        </div>
      )}

      {!category.subCategories && category.skus && <SkuCategoryItems skus={category.skus} short />}
      {category.subCategories &&
        category.subCategories.map((subCategory: ISubCategory) =>
          (() => {
            switch (true) {
              case !subCategory.types:
                return <SkuCategoryItem key={subCategory.id} sku={subCategory} />;
              case Boolean(subCategory.sku):
                return <SkuCategoryItems skus={[subCategory.sku!]} />;
              default:
                return (
                  <a key={subCategory.name} className={styles.skuLink} onClick={() => onSecondNavChange?.({ category, subCategory })}>
                    {subCategory.name}
                  </a>
                );
            }
          })(),
        )}
    </div>
  );
};

const SkuCategories = ({ setMenu, servicesMenu }: SkuCategoriesProps) => {
  const [secondNav, setSecondNav] = useState<ISecondNav>();
  const [showSecondNav, setShowSecondNav] = useState(false);

  if (!servicesMenu) return null;

  const wrapperRef = useRef(null);
  const onLocationChangeCloseNav = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  useOutsideClick(wrapperRef, setMenu);

  return (
    <div ref={wrapperRef} className={styles.navContainer} data-testid="navigation-menu-sku-categories-container">
      <div className={cn(styles.carousel, showSecondNav && styles.secondNav)}>
        <div className={styles.slide}>
          <div className={styles.slideHeader}>
            <h3 data-testid="navigation-menu-title">What can we help with? </h3>
          </div>
          <hr className={styles.divider} />
          <div className={cn(styles.categoriesColumns, styles.slideContent)}>
            {/* {columns} */}
            {servicesMenu.map((column, index) => (
              <div key={index} className={styles.categoryColumn} onClick={onLocationChangeCloseNav}>
                {column.categories &&
                  column.categories.map((category: ICategory) => (
                    <Category
                      key={category.name}
                      category={category}
                      onSecondNavChange={value => {
                        setSecondNav(value);
                        setShowSecondNav(true);
                      }}
                    />
                  ))}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.slide}>{secondNav && <SubCategorySlide {...secondNav} onSecondNavChange={() => setShowSecondNav(false)} />}</div>
      </div>
    </div>
  );
};

export default React.memo(SkuCategories);
